import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { login as loginApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail, AlertTriangle } from 'lucide-react';

const LOGO_URL = '/sturdfix-logo.jpg';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await loginApi(email, password);
      login(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.full_name}`);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Login failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-white" data-testid="login-page">
      <Card className="w-full max-w-sm shadow-lg border border-gray-100 rounded-2xl" data-testid="login-card">
        <CardContent className="pt-10 pb-8 px-8">
          {/* Logo centered */}
          <div className="flex justify-center mb-5">
            <img src={LOGO_URL} alt="Sturdfix" className="h-20 w-auto" data-testid="login-logo" />
          </div>

          {/* Brand name */}
          <h1 className="text-center text-2xl font-bold mb-1" style={{ color: '#0d1f6e' }} data-testid="login-brand">
            Sturdfix&trade; WMS
          </h1>

          {/* Company name */}
          <p className="text-center text-sm text-gray-500 mb-8" data-testid="login-company">
            Singhania International Ltd.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm" data-testid="login-error">
                <AlertTriangle size={16} />
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-3 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 h-11"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-3 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-10 h-11"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 text-sm font-semibold rounded-lg"
              style={{ backgroundColor: '#0d1f6e' }}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Version at very bottom */}
      <p className="text-xs text-gray-400 mt-8" data-testid="login-version">v1.0 · March 2026</p>
    </div>
  );
}
